import type { ExtendedProfile } from '../../hooks/profile';

export type HelpChatRole = 'user' | 'assistant';

export interface HelpChatMessage {
    role: HelpChatRole;
    content: string;
}

export function dashboardHelpChatBaseUrl(): string {
    const raw = (process.env.REACT_APP_DASHBOARD_HELP_CHAT_URL || 'https://help-dev.openeyes.com').trim();
    return raw.replace(/\/+$/, '');
}

export function dashboardHelpChatApiUrl(): string {
    return `${dashboardHelpChatBaseUrl()}/api/chat`;
}

/** Subset of profile safe to send to the help service (no tokens). */
export function buildDashboardHelpClientContext(
    profile: ExtendedProfile,
    pathnameWithSearch: string,
): { profile: Record<string, unknown>; pathname: string } {
    return {
        pathname: pathnameWithSearch || '/',
        profile: {
            user_id: profile.user_id,
            username: profile.username,
            email: profile.email,
            name: profile.name,
            role: profile.role,
            admin: profile.admin,
            language: profile.language,
            locale: profile.locale,
            timezone: profile.timezone,
            home_page: profile.home_page,
            customer: {
                name: profile.customer.name,
                display_name: profile.customer.display_name,
                country: profile.customer.settings?.country,
            },
        },
    };
}

export interface DashboardHelpChatRequestContext {
    profile: ExtendedProfile;
    pathnameWithSearch: string;
    accessToken: string | undefined;
    /** Base64 JPEG (no `data:` prefix), sent as `page_screenshot` for the last user turn only */
    pageScreenshotJpegBase64?: string | null;
}

const DEFAULT_HELP_CHAT_FETCH_TIMEOUT_MS = 30_000;

function helpChatFetchTimeoutMs(): number {
    const raw = process.env.REACT_APP_DASHBOARD_HELP_CHAT_TIMEOUT_MS;
    if (raw === undefined || raw === '') {
        return DEFAULT_HELP_CHAT_FETCH_TIMEOUT_MS;
    }
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : DEFAULT_HELP_CHAT_FETCH_TIMEOUT_MS;
}

export async function postDashboardHelpChat(
    messages: HelpChatMessage[],
    context: DashboardHelpChatRequestContext,
): Promise<{ reply?: string; error?: string }> {
    if (!context.accessToken?.trim()) {
        return { error: 'Sign in is required to use help chat.' };
    }
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${context.accessToken}`,
    };
    const client_context = buildDashboardHelpClientContext(context.profile, context.pathnameWithSearch);
    const payload: Record<string, unknown> = { messages, client_context };
    if (context.pageScreenshotJpegBase64) {
        payload.page_screenshot = context.pageScreenshotJpegBase64;
    }
    const controller = new AbortController();
    const timeoutMs = helpChatFetchTimeoutMs();
    const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);
    let res: Response;
    try {
        res = await fetch(dashboardHelpChatApiUrl(), {
            method: 'POST',
            headers,
            body: JSON.stringify(payload),
            signal: controller.signal,
        });
    } finally {
        window.clearTimeout(timeoutId);
    }
    const data = (await res.json()) as { reply?: string; error?: string };
    if (!res.ok) {
        return { error: data.error || `HTTP ${res.status}` };
    }
    return data;
}
